import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "./ui/hover-card";
import { useGlossary } from "../context/GlossaryContext";

/**
 * Renders text with any glossary term auto-linked to a hover card.
 * Falls back to plain text when the glossary isn't loaded yet.
 */
export default function GlossaryText({ children, className = "" }) {
  const { regex, terms } = useGlossary();
  const text = typeof children === "string" ? children : "";

  const slugByLower = useMemo(() => {
    const m = new Map();
    for (const t of terms) m.set(t.term.toLowerCase(), t);
    return m;
  }, [terms]);

  const segments = useMemo(() => {
    if (!regex || !text) return [{ type: "text", value: text }];
    const out = [];
    let last = 0;
    const fresh = new RegExp(regex.source, regex.flags); // exec needs fresh stateful regex
    let match;
    const seen = new Set(); // only auto-link first occurrence of each term
    while ((match = fresh.exec(text)) !== null) {
      const found = match[0];
      const lower = found.toLowerCase();
      if (seen.has(lower)) continue;
      const meta = slugByLower.get(lower);
      if (!meta) continue;
      seen.add(lower);
      if (match.index > last) {
        out.push({ type: "text", value: text.slice(last, match.index) });
      }
      out.push({ type: "term", value: found, meta });
      last = match.index + found.length;
    }
    if (last < text.length) out.push({ type: "text", value: text.slice(last) });
    return out;
  }, [regex, text, slugByLower]);

  if (!text) return null;

  return (
    <span className={className} style={{ whiteSpace: "pre-line" }} data-testid="glossary-text">
      {segments.map((s, i) =>
        s.type === "text" ? (
          <React.Fragment key={`t-${i}-${s.value.length}`}>{s.value}</React.Fragment>
        ) : (
          <TermHover key={`h-${i}-${s.value}`} term={s.value} meta={s.meta} />
        )
      )}
    </span>
  );
}

function TermHover({ term, meta }) {
  const [open, setOpen] = useState(false);
  return (
    <HoverCard openDelay={120} closeDelay={80} open={open} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>
        <span
          tabIndex={0}
          role="button"
          aria-label={`Definition of ${term}`}
          data-testid={`glossary-link-${meta.slug}`}
          className="underline decoration-dotted decoration-[#D18E63]/60 underline-offset-[3px] cursor-help text-[#2A2F2B] hover:text-[#D18E63] transition-soft"
          onClick={(e) => {
            // Toggle on tap (mobile)
            e.preventDefault();
            setOpen((v) => !v);
          }}
        >
          {term}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-80 rounded-2xl border-[#EAE6E1] p-4"
        side="top"
        align="start"
      >
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#D18E63] mb-1">
          Glossary
        </div>
        <div className="font-heading text-base text-[#2A2F2B] mb-1.5">{meta.term}</div>
        <p className="text-sm text-[#6B7068] leading-relaxed mb-3">{meta.summary}</p>
        <Link
          to="/learn"
          onClick={() => setOpen(false)}
          data-testid={`glossary-link-readmore-${meta.slug}`}
          className="text-xs text-[#547A8F] hover:underline"
        >
          Read more in the Learn hub →
        </Link>
      </HoverCardContent>
    </HoverCard>
  );
}
